import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, FileText, FileDown, ExternalLink, PenLine, ArrowDown } from "lucide-react";
import { AdoptSignature } from "@/components/fbsign/AdoptSignature";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const today = () => new Date().toLocaleDateString("en-GB");

// Public FBSign signing page — no login; access is the recipient's unique token.
export default function FBSignPublic() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [sig, setSig] = useState(null); // { name, png }
  const [title, setTitle] = useState("");
  const [typedName, setTypedName] = useState("");
  const [adoptOpen, setAdoptOpen] = useState(false);
  const load = () => axios.get(`${BACKEND}/api/public/fbsign/${token}`).then((r) => setD(r.data)).catch((e) => setErr(e.response?.data?.detail || "This signing link is not valid."));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [token]);

  if (err) return <Center><FileText size={40} className="mx-auto text-[var(--ink-2)] mb-3" /><h1 className="text-xl font-serif-strong mb-2">Link unavailable</h1><p className="text-sm text-[var(--ink-2)]" data-testid="fbsign-public-error">{err}</p></Center>;
  if (!d) return <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]"><Loader2 size={28} className="animate-spin text-[var(--navy)]" /></div>;
  if (d.signed) {
    return (
      <Center testid="fbsign-public-done">
        <CheckCircle2 size={48} className="mx-auto text-[var(--ok)] mb-3" />
        <h1 className="text-xl font-serif-strong mb-2">Document signed</h1>
        <p className="text-sm text-[var(--ink-2)] mb-4">Thank you{d.signer_name ? `, ${d.signer_name}` : ""}. Your signature on <b>{d.name}</b> has been recorded{d.signed_at ? ` on ${new Date(d.signed_at).toLocaleDateString("en-GB")}` : ""}.</p>
        {d.completed
          ? <a data-testid="fbsign-public-download" href={`${BACKEND}/api/public/fbsign/${token}/signed-pdf`} className="btn-ghost inline-flex items-center gap-1.5 text-xs !py-1.5 !px-3"><FileDown size={13} /> Download signed copy</a>
          : <p className="text-xs text-[var(--ink-2)]">Once all parties have signed, the signed copy will be available from this link.</p>}
      </Center>
    );
  }
  const needsTitle = (d.fields || []).some((f) => f.type === "title");
  const remaining = (d.fields || [])
    .filter((f) => (f.type === "signature" && !sig) || (f.type === "title" && !title.trim()) || (f.type === "name" && typedName.trim().length < 2))
    .sort((a, b) => a.page - b.page || a.y - b.y || a.x - b.x);
  return (
    <div className="min-h-screen bg-[var(--bg)] pt-8 pb-28 px-4" data-testid="fbsign-public">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-5">
          <img src="/brand/logo.png" alt="Finchley Business Services" className="h-10 w-auto" />
          <div>
            <div className="font-serif-strong text-lg">{d.name}</div>
            <div className="text-xs text-[var(--ink-2)]">Sent by {d.sender} · for {d.recipient_name}</div>
          </div>
        </div>
        {d.message && <div className="card-flat p-4 mb-4 text-sm whitespace-pre-line bg-[var(--bg-2)]" data-testid="fbsign-public-message">{d.message}</div>}
        <div className="card-flat mb-4 overflow-hidden">
          {d.pages ? (
            <div className="bg-[var(--bg-2)] p-3 space-y-3" data-testid="fbsign-public-pages">
              {Array.from({ length: d.pages.count }).map((_, i) => (
                <div key={i} className="relative mx-auto bg-white shadow-sm" style={{ maxWidth: 680, aspectRatio: `${d.pages.sizes[i].w} / ${d.pages.sizes[i].h}` }}>
                  <img src={`${BACKEND}/api/public/fbsign/${token}/pages/${i}`} alt={`Page ${i + 1}`} className="w-full h-full block" />
                  {d.fields.filter((f) => f.page === i).map((f) => (
                    <PlacedField key={f.id} f={f} sig={sig} text={{ title, name: typedName }} setText={{ title: setTitle, name: setTypedName }} onSign={() => setAdoptOpen(true)} />
                  ))}
                </div>
              ))}
            </div>
          ) : d.file_type === "pdf" ? (
            <>
              <iframe title="document" src={`${BACKEND}/api/public/fbsign/${token}/file`} className="w-full h-[70vh] bg-white" data-testid="fbsign-public-pdf" />
              <a href={`${BACKEND}/api/public/fbsign/${token}/file`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 text-xs py-2 border-t border-[var(--line)] text-[var(--navy)]"><ExternalLink size={12} /> Open the document full screen</a>
            </>
          ) : (
            <div className="prose max-w-none text-sm p-6 max-h-[70vh] overflow-y-auto" data-testid="fbsign-public-html" dangerouslySetInnerHTML={{ __html: d.html || "" }} />
          )}
        </div>
      </div>
      <AdoptSignature open={adoptOpen} onOpenChange={setAdoptOpen} defaultName={sig?.name || d.recipient_name}
        onAdopt={(s) => { setSig(s); if (!typedName.trim()) setTypedName(s.name); setAdoptOpen(false); }} />
      <FinishBar token={token} sig={sig} title={title} typedName={typedName} needsTitle={needsTitle} hasFields={!!d.pages} remaining={remaining}
        onSign={() => setAdoptOpen(true)} onSigned={load} />
    </div>
  );
}

function PlacedField({ f, sig, text, setText, onSign }) {
  const box = { left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%` };
  const pending = { ...box, border: "1.5px dashed #D97706", background: "#FEF3C7cc", color: "#92400E" };
  const tid = `fbsign-public-field-${f.id}`;
  if (f.type === "title" || f.type === "name") {
    const v = text[f.type];
    return <input data-testid={tid} value={v} onChange={(e) => setText[f.type](e.target.value)} placeholder={f.type === "name" ? "Full name" : "Title"} maxLength={120}
      className="absolute px-1 text-[11px] outline-none focus:ring-1 focus:ring-[var(--navy)]" style={v ? { ...box, border: "1px solid #CBD5E1", background: "#fff" } : pending} />;
  }
  if (f.type === "signature") {
    return sig
      ? <button data-testid={tid} onClick={onSign} title="Change signature" className="absolute flex items-center justify-center hover:bg-[#EEF2FF] transition-colors" style={{ ...box, border: "1px solid #CBD5E1" }}>
          <img src={sig.png} alt="Your signature" className="max-h-full max-w-full object-contain" />
        </button>
      : <button data-testid={tid} onClick={onSign} className="absolute flex flex-col items-center justify-center text-[10px] font-semibold animate-pulse hover:animate-none" style={pending}>
          <PenLine size={12} /> Sign
        </button>;
  }
  return <div data-testid={tid} className={`absolute flex items-center px-1 text-[11px] ${sig ? "" : "font-semibold justify-center"}`}
    style={sig ? { ...box, color: "#0F1B3D" } : pending}>{sig ? today() : "Date"}</div>;
}

function FinishBar({ token, sig, title, typedName, needsTitle, hasFields, remaining, onSign, onSigned }) {
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  function goNext() {
    setErr("");
    const el = document.querySelector(`[data-testid="fbsign-public-field-${remaining[0].id}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.remove("fbsign-flash"); void el.offsetWidth; el.classList.add("fbsign-flash");
    if (el.tagName === "INPUT") setTimeout(() => el.focus({ preventScroll: true }), 350);
  }

  async function submit() {
    setErr("");
    if (!sig) { setErr(hasFields ? "Click a Sign box on the document to adopt your signature." : "Click Sign to adopt your signature."); return; }
    if (needsTitle && !title.trim()) { setErr("Please enter your title on the document."); return; }
    if (!accepted) { setErr("Please tick the box to agree."); return; }
    setBusy(true);
    try {
      await axios.post(`${BACKEND}/api/public/fbsign/${token}/sign`, { signer_name: typedName.trim() || sig.name, signer_title: title.trim() || null, signature_png: sig.png, accepted: true });
      await onSigned();
    } catch (e) { setErr(e.response?.data?.detail || "Could not submit your signature. Please try again."); }
    finally { setBusy(false); }
  }
  return (
    <div className="fixed bottom-0 inset-x-0 bg-white border-t border-[var(--line)] shadow-[0_-4px_16px_rgba(0,0,0,0.06)] z-40" data-testid="fbsign-sign-box">
      <div className="max-w-3xl mx-auto px-4 py-3 flex flex-wrap items-center gap-3">
        {!hasFields && (
          <button data-testid="fbsign-open-adopt" onClick={onSign} className="flex items-center gap-2 border border-dashed border-[var(--gold,#D97706)] px-3 py-1.5 rounded-sm text-sm hover:bg-[#FEF3C7] transition-colors">
            {sig ? <img src={sig.png} alt="Your signature" className="h-8" /> : <><PenLine size={14} /> Sign</>}
          </button>
        )}
        <label className="flex items-start gap-2 cursor-pointer flex-1 min-w-[220px]">
          <input type="checkbox" data-testid="fbsign-accept" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5" />
          <span className="text-xs">I have reviewed this document and agree to sign it electronically. I understand my electronic signature is legally binding.</span>
        </label>
        {remaining.length > 0 ? (
          <button data-testid="fbsign-next" onClick={goNext} className="btn-primary flex items-center gap-2 text-sm !py-2.5 !px-5" style={{ background: "#C9A227", borderColor: "#C9A227", color: "#0E1A3A" }}>
            Next <ArrowDown size={14} /> <span className="text-[10px] opacity-80" data-testid="fbsign-next-count">{remaining.length} left</span>
          </button>
        ) : (
          <button data-testid="fbsign-submit" onClick={submit} disabled={busy} className="btn-primary flex items-center gap-2 text-sm !py-2.5 !px-5">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} {busy ? "Submitting…" : "Finish"}
          </button>
        )}
        {err && <div className="w-full p-2 bg-[var(--err-bg)] border border-[var(--err)] text-[var(--err)] rounded text-xs" data-testid="fbsign-sign-error">{err}</div>}
      </div>
    </div>
  );
}

function Center({ children, testid }) {
  return <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] p-4"><div className="card-flat p-8 max-w-md text-center" data-testid={testid}>{children}</div></div>;
}
